import { useState, useEffect } from "react";
import {
  Sidebar,
  SidebarTrigger,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarFooter,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/components/blocks/evo-sidebar";
import { Settings, ChevronRight, Shield, FileText, Bell, TrendingUp } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import { useFavorites } from "./hooks/useFavorites";
import { DatabaseSwitcher } from "./components/DatabaseSwitcher";
import { FavoritesSection } from "./components/FavoritesSection";
import { MenuItemWithContext } from "./components/MenuItemWithContext";
import type { LeftMenuProps, MenuItem } from "./types";
import { menuItems, databases, menuConfig } from "./config/menuItems";
import { useAuth } from "@/providers/AuthProvider";
import { useDataMode } from "@/contexts/DataModeContext";

/**
 * Left Menu Component (Modular Version)
 * 
 * Features:
 * - Collapsible sidebar (icon-only mode)
 * - Favorites system (disabled - only 9 pages, threshold is 10)
 * - Database/Environment switcher (disabled - company selector in header)
 * - Context menu for favoriting items
 * - Mobile responsive
 * - Keyboard shortcuts (Cmd/Ctrl + B)
 */
export function LeftMenuModular({ activePage, onPageChange }: LeftMenuProps) {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { authenticatedUser, isProduction } = useAuth();
  const { isDemoMode } = useDataMode();
  // Settings section is only visible to users with "Administrator" ApplicationRole.
  // Always show in demo mode (so users can explore settings with sample data),
  // in non-production mode (dev/sandbox), or when the authenticated user is an Administrator.
  const isAdmin = isDemoMode || !isProduction || authenticatedUser?.applicationRole === "Administrator";

  const [selectedDatabase, setSelectedDatabase] = useState("production");
  const [activeItemId, setActiveItemId] = useState<string>("");
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({
    "resources-menu": false, // Resources menu collapsed by default
    "reports-menu": false, // Reports menu collapsed by default
    "settings": false, // Settings menu collapsed by default
  });

  const favoritesEnabled = menuConfig.totalPages >= menuConfig.favoritesThreshold;
  const { favorites, toggleFavorite, isFavorite } = useFavorites(favoritesEnabled);

  const favoriteItems = menuItems.filter((item) => isFavorite(item.id));

  // Sync activeItemId with activePage prop
  useEffect(() => {
    // Map page names to their menu item IDs
    const pageToItemId: Record<string, string> = {
      'dashboard': 'main-dashboard',
      'projects': 'main-projects',
      'project-templates': 'main-project-templates',
      'portfolio': 'main-portfolio',
      'directory': 'sub-directory',
      'roles-rates': 'sub-roles-rates',
      'resources': 'sub-resources',
      'utilisation': 'sub-utilisation',
      'skills': 'sub-skills',
      'project-transactions': 'sub-project-transactions',
      'wip-reporting': 'sub-wip-reporting',
      'audit-log': 'sub-audit-log',
      'profitability': 'main-profitability',
      'budgets': 'main-budgets',
      'gantt': 'main-gantt',
      'settings-general': 'sub-settings-general',
      'settings-project': 'sub-settings-project',
      'settings-user-preferences': 'sub-settings-user-preferences',
      'settings-integrations': 'sub-settings-integrations',
      'settings-feature-labs': 'sub-settings-feature-labs',
      'settings-user-permissions': 'sub-settings-user-permissions',
      'settings-templates': 'sub-settings-templates',
      'settings-notifications': 'sub-settings-notifications',
      'settings-finance': 'sub-settings-finance',
    };
    
    const itemId = pageToItemId[activePage];
    if (itemId) {
      setActiveItemId(itemId);
      
      // Auto-open parent menus for sub-items
      if (itemId.startsWith('sub-settings-')) {
        setOpenMenus(prev => ({ ...prev, 'settings': true }));
      } else if (itemId.startsWith('sub-') && activePage.includes('directory')) {
        setOpenMenus(prev => ({ ...prev, 'resources-menu': true }));
      } else if (itemId.startsWith('sub-') && activePage.includes('roles-rates')) {
        setOpenMenus(prev => ({ ...prev, 'resources-menu': true }));
      } else if (itemId.startsWith('sub-resources')) {
        setOpenMenus(prev => ({ ...prev, 'resources-menu': true }));
      } else if (itemId.startsWith('sub-utilisation')) {
        setOpenMenus(prev => ({ ...prev, 'resources-menu': true }));
      } else if (itemId.startsWith('sub-skills')) {
        setOpenMenus(prev => ({ ...prev, 'resources-menu': true }));
      } else if (itemId.startsWith('sub-project-transactions') || itemId.startsWith('sub-wip-reporting') || itemId.startsWith('sub-audit-log')) {
        setOpenMenus(prev => ({ ...prev, 'reports-menu': true }));
      }
    }
  }, [activePage]);

  const handleItemClick = (page: string, itemId: string) => {
    setActiveItemId(itemId);
    onPageChange(page);
    
    // Close Resources, Reports, and Settings menus when clicking on any main menu item (not sub-items)
    if (!itemId.startsWith('sub-')) {
      setOpenMenus(prev => ({
        ...prev,
        "resources-menu": false,
        "reports-menu": false,
        "settings": false
      }));
    }
  };

  const toggleMenu = (menuId: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const renderMenuItem = (item: MenuItem, uniqueId: string, isFavoriteItem: boolean = false) => {
    const isActive = activeItemId === uniqueId;
    return (
      <MenuItemWithContext
        item={item}
        uniqueId={uniqueId}
        isActive={isActive}
        isFavoriteItem={isFavoriteItem}
        isFavorite={isFavorite(item.id)}
        onItemClick={handleItemClick}
        onToggleFavorite={toggleFavorite}
        favoritesEnabled={favoritesEnabled}
      />
    );
  };

  return (
    <Sidebar variant="floating" collapsible="icon" className="relative h-full">
      {/* Header */}
      <SidebarHeader>
        <div className="flex items-center gap-2">
          <SidebarTrigger />
          <h3 className="font-semibold truncate whitespace-nowrap max-w-[195px] group-data-[state=collapsed]:hidden text-neutral-700">
            Projects & Billing
          </h3>
        </div>
      </SidebarHeader>
      <div className="border-t border-neutral-200 mx-2 group-data-[state=collapsed]:show"></div>

      {/* Content */}
      <SidebarContent className="group-data-[state=collapsed]:items-center">
        {/* Favorites Section - Only shows if enabled (currently disabled: 9 pages < 10 threshold) */}
        {favoritesEnabled && (
          <FavoritesSection favoriteItems={favoriteItems} renderMenuItem={renderMenuItem} />
        )}

        {/* Main Navigation Section */}
        <SidebarGroup className="group-data-[state=collapsed]:items-center">
          <SidebarGroupContent className="group-data-[state=collapsed]:w-8">
            <SidebarMenu className="gap-0.5 group-data-[state=collapsed]:w-full [&_span]:transition-all [&_span]:duration-200 [&_span]:ease-out [&_span]:opacity-100 [&_span]:translate-x-0 group-data-[state=collapsed]:[&_span]:opacity-0 group-data-[state=collapsed]:[&_span]:-translate-x-2 group-data-[state=collapsed]:[&_span]:pointer-events-none">
              
              {/* Dashboard */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "dashboard")!, "main-dashboard")}
              </SidebarMenuItem>

              {/* Projects */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "projects")!, "main-projects")}
              </SidebarMenuItem>

              {/* Project Templates */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "project-templates")!, "main-project-templates")}
              </SidebarMenuItem>

              {/* Portfolio */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "portfolio")!, "main-portfolio")}
              </SidebarMenuItem>

              {/* Resources Menu (Collapsible with sub-items / Flyout when collapsed) */}
              {(() => {
                const resourcesMenu = menuItems.find((item) => item.id === "resources-menu")!;
                const ResourcesIcon = resourcesMenu.icon;
                const DirectoryIcon = resourcesMenu.children![0].icon;
                const RolesRatesIcon = resourcesMenu.children![1].icon;
                const ResourceAllocationIcon = resourcesMenu.children![2].icon;
                const UtilisationIcon = resourcesMenu.children![3].icon;
                const SkillsIcon = resourcesMenu.children![4].icon;

                if (isCollapsed) {
                  return (
                    <SidebarMenuItem>
                      <DropdownMenu>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <DropdownMenuTrigger asChild>
                              <SidebarMenuButton hasSubmenu>
                                <ResourcesIcon />
                              </SidebarMenuButton>
                            </DropdownMenuTrigger>
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <span className="flex items-center gap-1">
                              Resources
                              <ChevronRight className="size-3 opacity-60" />
                            </span>
                          </TooltipContent>
                        </Tooltip>
                        <DropdownMenuContent side="right" align="start" className="min-w-[180px]">
                          <DropdownMenuLabel>Resources</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleItemClick("directory", "sub-directory")}
                            className={activeItemId === "sub-directory" ? "bg-accent font-medium" : ""}
                          >
                            <DirectoryIcon className="size-4" />
                            Directory
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleItemClick("roles-rates", "sub-roles-rates")}
                            className={activeItemId === "sub-roles-rates" ? "bg-accent font-medium" : ""}
                          >
                            <RolesRatesIcon className="size-4" />
                            Roles & Rates
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleItemClick("resources", "sub-resources")}
                            className={activeItemId === "sub-resources" ? "bg-accent font-medium" : ""}
                          >
                            <ResourceAllocationIcon className="size-4" />
                            Resource Allocation
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleItemClick("utilisation", "sub-utilisation")}
                            className={activeItemId === "sub-utilisation" ? "bg-accent font-medium" : ""}
                          >
                            <UtilisationIcon className="size-4" />
                            Utilisation
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleItemClick("skills", "sub-skills")}
                            className={activeItemId === "sub-skills" ? "bg-accent font-medium" : ""}
                          >
                            <SkillsIcon className="size-4" />
                            Skills
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </SidebarMenuItem>
                  );
                }
                
                return (
                  <SidebarMenuItem>
                    <Collapsible
                      open={openMenus["resources-menu"]}
                      onOpenChange={() => toggleMenu("resources-menu")}
                      className="group/collapsible"
                    >
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip="Resources" hasSubmenu>
                          <ResourcesIcon />
                          <span className="group-data-[collapsible=icon]:hidden">Resources</span>
                          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {/* Directory */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("directory", "sub-directory")}
                              data-active={activeItemId === "sub-directory"}
                              className={activeItemId === "sub-directory" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <DirectoryIcon />
                              <span>Directory</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {/* Roles & Rates */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("roles-rates", "sub-roles-rates")}
                              data-active={activeItemId === "sub-roles-rates"}
                              className={activeItemId === "sub-roles-rates" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <RolesRatesIcon />
                              <span>Roles & Rates</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {/* Resource Allocation */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("resources", "sub-resources")}
                              data-active={activeItemId === "sub-resources"}
                              className={activeItemId === "sub-resources" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <ResourceAllocationIcon />
                              <span>Resource Allocation</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {/* Utilisation */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("utilisation", "sub-utilisation")}
                              data-active={activeItemId === "sub-utilisation"}
                              className={activeItemId === "sub-utilisation" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <UtilisationIcon />
                              <span>Utilisation</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {/* Skills */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("skills", "sub-skills")}
                              data-active={activeItemId === "sub-skills"}
                              className={activeItemId === "sub-skills" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <SkillsIcon />
                              <span>Skills</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </Collapsible>
                  </SidebarMenuItem>
                );
              })()}

              {/* Reports Menu (Collapsible with sub-items / Flyout when collapsed) */}
              {(() => {
                const reportsMenu = menuItems.find((item) => item.id === "reports-menu")!;
                const ReportsIcon = reportsMenu.icon;
                const ProjectTransactionsIcon = reportsMenu.children![0].icon;
                const WIPIcon = reportsMenu.children![1].icon;
                const AuditLogIcon = reportsMenu.children![2].icon;

                if (isCollapsed) {
                  return (
                    <SidebarMenuItem>
                      <DropdownMenu>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <DropdownMenuTrigger asChild>
                              <SidebarMenuButton hasSubmenu>
                                <ReportsIcon />
                              </SidebarMenuButton>
                            </DropdownMenuTrigger>
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <span className="flex items-center gap-1">
                              Reports
                              <ChevronRight className="size-3 opacity-60" />
                            </span>
                          </TooltipContent>
                        </Tooltip>
                        <DropdownMenuContent side="right" align="start" className="min-w-[180px]">
                          <DropdownMenuLabel>Reports</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleItemClick("project-transactions", "sub-project-transactions")}
                            className={activeItemId === "sub-project-transactions" ? "bg-accent font-medium" : ""}
                          >
                            <ProjectTransactionsIcon className="size-4" />
                            Project Transactions
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleItemClick("wip-reporting", "sub-wip-reporting")}
                            className={activeItemId === "sub-wip-reporting" ? "bg-accent font-medium" : ""}
                          >
                            <WIPIcon className="size-4" />
                            WIP
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleItemClick("audit-log", "sub-audit-log")}
                            className={activeItemId === "sub-audit-log" ? "bg-accent font-medium" : ""}
                          >
                            <AuditLogIcon className="size-4" />
                            Audit Log
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </SidebarMenuItem>
                  );
                }
                
                return (
                  <SidebarMenuItem>
                    <Collapsible
                      open={openMenus["reports-menu"]}
                      onOpenChange={() => toggleMenu("reports-menu")}
                      className="group/collapsible"
                    >
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip="Reports" hasSubmenu>
                          <ReportsIcon />
                          <span className="group-data-[collapsible=icon]:hidden">Reports</span>
                          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {/* Project Transactions */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("project-transactions", "sub-project-transactions")}
                              data-active={activeItemId === "sub-project-transactions"}
                              className={activeItemId === "sub-project-transactions" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <ProjectTransactionsIcon />
                              <span>Project Transactions</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {/* WIP (Work In Progress) */}
                          <SidebarMenuSubItem>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SidebarMenuSubButton
                                    onClick={() => handleItemClick("wip-reporting", "sub-wip-reporting")}
                                    data-active={activeItemId === "sub-wip-reporting"}
                                    className={activeItemId === "sub-wip-reporting" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                                  >
                                    <WIPIcon />
                                    <span>WIP</span>
                                  </SidebarMenuSubButton>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>Work In Progress</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </SidebarMenuSubItem>
                          {/* Audit Log */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("audit-log", "sub-audit-log")}
                              data-active={activeItemId === "sub-audit-log"}
                              className={activeItemId === "sub-audit-log" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <AuditLogIcon />
                              <span>Audit Log</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </Collapsible>
                  </SidebarMenuItem>
                );
              })()}

              {/* Profitability */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "profitability")!, "main-profitability")}
              </SidebarMenuItem>

              {/* Budgets & Variance */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "budgets")!, "main-budgets")}
              </SidebarMenuItem>

              {/* Gantt Charts */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "gantt")!, "main-gantt")}
              </SidebarMenuItem>

            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter>
        {/* Settings Menu (Collapsible with sub-items / Flyout when collapsed) - only visible to Administrators */}
        {menuConfig.showSettings && isAdmin && (
          <SidebarMenu>
            <SidebarMenuItem>
              {isCollapsed ? (
                <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <SidebarMenuButton hasSubmenu>
                          <Settings />
                        </SidebarMenuButton>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <span className="flex items-center gap-1">
                        Settings
                        <ChevronRight className="size-3 opacity-60" />
                      </span>
                    </TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent side="right" align="end" className="min-w-[180px]">
                    <DropdownMenuLabel>Settings</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleItemClick("settings-general", "sub-settings-general")}
                      className={activeItemId === "sub-settings-general" ? "bg-accent font-medium" : ""}
                    >
                      <Settings className="size-4" />
                      General Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleItemClick("settings-project", "sub-settings-project")}
                      className={activeItemId === "sub-settings-project" ? "bg-accent font-medium" : ""}
                    >
                      <Settings className="size-4" />
                      Project Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleItemClick("settings-user-preferences", "sub-settings-user-preferences")}
                      className={activeItemId === "sub-settings-user-preferences" ? "bg-accent font-medium" : ""}
                    >
                      <Settings className="size-4" />
                      User Preferences
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleItemClick("settings-integrations", "sub-settings-integrations")}
                      className={activeItemId === "sub-settings-integrations" ? "bg-accent font-medium" : ""}
                    >
                      <Settings className="size-4" />
                      Integrations
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleItemClick("settings-feature-labs", "sub-settings-feature-labs")}
                      className={activeItemId === "sub-settings-feature-labs" ? "bg-accent font-medium" : ""}
                    >
                      <Settings className="size-4" />
                      Feature Labs
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleItemClick("settings-user-permissions", "sub-settings-user-permissions")}
                      className={activeItemId === "sub-settings-user-permissions" ? "bg-accent font-medium" : ""}
                    >
                      <Shield className="size-4" />
                      User Permissions
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleItemClick("settings-templates", "sub-settings-templates")}
                      className={activeItemId === "sub-settings-templates" ? "bg-accent font-medium" : ""}
                    >
                      <FileText className="size-4" />
                      Templates
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleItemClick("settings-notifications", "sub-settings-notifications")}
                      className={activeItemId === "sub-settings-notifications" ? "bg-accent font-medium" : ""}
                    >
                      <Bell className="size-4" />
                      Notifications
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleItemClick("settings-finance", "sub-settings-finance")}
                      className={activeItemId === "sub-settings-finance" ? "bg-accent font-medium" : ""}
                    >
                      <TrendingUp className="size-4" />
                      Finance Settings
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Collapsible
                  open={openMenus["settings"]}
                  onOpenChange={() => toggleMenu("settings")}
                  className="group/collapsible"
                >
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip="Settings" hasSubmenu>
                      <Settings />
                      <span>Settings</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {/* General Settings */}
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          onClick={() => handleItemClick("settings-general", "sub-settings-general")}
                          data-active={activeItemId === "sub-settings-general"}
                          className={activeItemId === "sub-settings-general" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                        >
                          <Settings />
                          <span>General Settings</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      {/* Project Settings */}
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          onClick={() => handleItemClick("settings-project", "sub-settings-project")}
                          data-active={activeItemId === "sub-settings-project"}
                          className={activeItemId === "sub-settings-project" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                        >
                          <Settings />
                          <span>Project Settings</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      {/* User Preferences */}
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          onClick={() => handleItemClick("settings-user-preferences", "sub-settings-user-preferences")}
                          data-active={activeItemId === "sub-settings-user-preferences"}
                          className={activeItemId === "sub-settings-user-preferences" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                        >
                          <Settings />
                          <span>User Preferences</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      {/* Integrations */}
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          onClick={() => handleItemClick("settings-integrations", "sub-settings-integrations")}
                          data-active={activeItemId === "sub-settings-integrations"}
                          className={activeItemId === "sub-settings-integrations" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                        >
                          <Settings />
                          <span>Integrations</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>

                      {/* Feature Labs */}
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          onClick={() => handleItemClick("settings-feature-labs", "sub-settings-feature-labs")}
                          data-active={activeItemId === "sub-settings-feature-labs"}
                          className={activeItemId === "sub-settings-feature-labs" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                        >
                          <Settings />
                          <span>Feature Labs</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>

                      {/* User Permissions */}
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          onClick={() => handleItemClick("settings-user-permissions", "sub-settings-user-permissions")}
                          data-active={activeItemId === "sub-settings-user-permissions"}
                          className={activeItemId === "sub-settings-user-permissions" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                        >
                          <Shield />
                          <span>User Permissions</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>

                      {/* Templates */}
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          onClick={() => handleItemClick("settings-templates", "sub-settings-templates")}
                          data-active={activeItemId === "sub-settings-templates"}
                          className={activeItemId === "sub-settings-templates" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                        >
                          <FileText />
                          <span>Templates</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>

                      {/* Notifications */}
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          onClick={() => handleItemClick("settings-notifications", "sub-settings-notifications")}
                          data-active={activeItemId === "sub-settings-notifications"}
                          className={activeItemId === "sub-settings-notifications" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                        >
                          <Bell />
                          <span>Notifications</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>

                      {/* Finance Settings */}
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          onClick={() => handleItemClick("settings-finance", "sub-settings-finance")}
                          data-active={activeItemId === "sub-settings-finance"}
                          className={activeItemId === "sub-settings-finance" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                        >
                          <TrendingUp />
                          <span>Finance Settings</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </SidebarMenuItem>
          </SidebarMenu>
        )}

        {/* Database Switcher - Not used (company selector in header) */}
        {menuConfig.showDatabaseSwitcher && (
          <>
            <div className="border-t border-neutral-200 mb-2 mt-2 group-data-[state=collapsed]:hidden"></div>
            <DatabaseSwitcher
              databases={databases}
              selectedDatabase={selectedDatabase}
              onDatabaseChange={setSelectedDatabase}
              label="Database"
            />
          </>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
